import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import { ArrowLeft, Download, TrendingUp, BarChart3, DollarSign, Calendar, Eye, EyeOff, Package } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logo from "../images/tvpLogo.png";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

const getMonthName = (monthIndex) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return monthNames[monthIndex];
};

function PaidoutReport() {
  const [currentReportType, setCurrentReportType] = useState("daily");
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const navigate = useNavigate();
  const goBack = () => navigate("/admin/reports");
  
  // Get the most recent Monday
  const getMostRecentMonday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().slice(0, 10);
  };
  
  const [weekStartDate, setWeekStartDate] = useState(getMostRecentMonday());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [showTable, setShowTable] = useState(false);
  const pdfRef = useRef();

  const reportTypes = [
    { id: "daily", label: "Daily" },
    { id: "weekly", label: "Weekly" },
    { id: "range", label: "Date Range" },
    { id: "monthly", label: "Monthly" }
  ];

  // Calculate Sunday date from Monday
  const calculateSundayFromMonday = (mondayDate) => {
    const monday = new Date(mondayDate);
    const sunday = new Date(monday.setDate(monday.getDate() + 6));
    return sunday.toISOString().slice(0, 10);
  };

  // Check if date is Monday
  const isMondayDate = (dateString) => {
    const date = new Date(dateString);
    return date.getDay() === 1;
  };

  // Get all Mondays in a reasonable range for the date picker
  const getMondayDates = () => {
    const mondays = [];
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 2); // 2 years back
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 2); // 2 years forward

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === 1) {
        mondays.push(new Date(d).toISOString().slice(0, 10));
      }
    }
    return mondays;
  };

  const fetchPaidoutReport = async (reportType = currentReportType) => {
    setIsLoading(true);
    setReportData(null);

    let url = `/admin/paidout/`;

    try {
      if (reportType === "daily") {
        url += `daily/${selectedDate}`;
      } else if (reportType === "weekly") {
        if (!weekStartDate) {
          alert("Please select a Monday date");
          setIsLoading(false);
          return;
        }
        const sundayDate = calculateSundayFromMonday(weekStartDate);
        url += `weekly/${weekStartDate}/${sundayDate}`;
      } else if (reportType === "range") {
        const [rangeStart, rangeEnd] = getDateRangeForRange();
        if (!rangeStart || !rangeEnd) {
          alert("Please select both start and end dates");
          setIsLoading(false);
          return;
        }
        url += `range/${rangeStart}/${rangeEnd}`;
      } else if (reportType === "monthly") {
        if (!selectedYear || !selectedMonth) {
          alert("Please select year and month");
          setIsLoading(false);
          return;
        }
        url += `monthly/${selectedYear}/${selectedMonth}`;
      }

      const res = await axiosInstance.get(url);
      setReportData(res.data);
    } catch (err) {
      console.error(`Error fetching ${reportType} paidout report:`, err);
      alert(`Failed to fetch ${reportType} paidout report.`);
    } finally {
      setIsLoading(false);
    }
  };

  // For range report type
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const getDateRangeForRange = () => {
    return [rangeStart, rangeEnd];
  };

  const handleReportTypeChange = (type) => {
    setCurrentReportType(type);
    setReportData(null);
    setShowTable(false);
  };

  useEffect(() => {
    fetchPaidoutReport();
  }, [currentReportType]);

  useEffect(() => {
    if (reportData && currentReportType !== "daily") {
      fetchPaidoutReport();
    }
  }, [selectedDate, weekStartDate, rangeStart, rangeEnd, selectedYear, selectedMonth]);

  const generatePDF = async () => {
    const input = pdfRef.current;
    input.style.display = "block";
    await new Promise((res) => setTimeout(res, 500));
    const canvas = await html2canvas(input, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;
    while (heightLeft > 20) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    pdf.save("Paidout_Report.pdf");
    input.style.display = "none";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4 py-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <button
            onClick={goBack}
            className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-all mb-4 md:mb-6 font-medium text-sm md:text-base"
            style={{ fontFamily: "Bambino" }}
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Reports</span>
          </button>

          <div className="flex items-center justify-between gap-2">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-orange-600 via-red-500 to-pink-500 bg-clip-text text-transparent" style={{ fontFamily: "Bambino", lineHeight: "1.2" }}>
              Paidout Report
            </h1>
            <span>£</span>
          </div>
        </div>

        {/* Report Type Switcher */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-3 md:p-4 shadow-xl mb-6 md:mb-8 border-2 border-purple-100">
          <div className="flex flex-wrap gap-2 md:gap-3">
            {reportTypes.map((report) => (
              <button
                key={report.id}
                onClick={() => handleReportTypeChange(report.id)}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all text-sm md:text-base ${currentReportType === report.id
                  ? 'bg-gradient-to-r from-orange-400 to-red-400 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-purple-200 hover:border-purple-400'
                  }`}
                style={{ fontFamily: "Bambino" }}
              >
                {report.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Selection Card */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl mb-6 md:mb-8 border-2 border-purple-100">
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 md:gap-4">
            {currentReportType === "daily" && (
              <>
                <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Select Date:</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full sm:w-auto"
                  style={{ fontFamily: "Bambino" }}
                />
              </>
            )}
            {(currentReportType === "weekly" || currentReportType === "range") && (
              <>
                {currentReportType === "weekly" && (
                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                    <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Select Monday:</label>
                    <select
                      value={weekStartDate}
                      onChange={(e) => setWeekStartDate(e.target.value)}
                      className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full"
                      style={{ fontFamily: "Bambino" }}
                    >
                      {getMondayDates().map((monday) => (
                        <option key={monday} value={monday}>
                          {new Date(monday).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {currentReportType === "range" && (
                  <>
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                      <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Start Date:</label>
                      <input
                        type="date"
                        value={rangeStart}
                        onChange={(e) => setRangeStart(e.target.value)}
                        className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                      <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>End Date:</label>
                      <input
                        type="date"
                        value={rangeEnd}
                        onChange={(e) => setRangeEnd(e.target.value)}
                        className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            {currentReportType === "monthly" && (
              <>
                <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                  <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Year:</label>
                  <input
                    type="number"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 w-28 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base"
                    style={{ fontFamily: "Bambino" }}
                  />
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                  <label className="font-semibold text-gray-700 text-sm md:text-base whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Month:</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="border-2 border-purple-200 rounded-lg md:rounded-xl px-3 md:px-4 py-2 md:py-3 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all text-sm md:text-base w-full"
                    style={{ fontFamily: "Bambino" }}
                  >
                    {[...Array(12).keys()].map(i => (
                      <option key={i} value={i + 1}>{getMonthName(i)}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <button
              onClick={generatePDF}
              className="bg-gradient-to-r from-blue-400 to-cyan-400 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl hover:from-blue-500 hover:to-cyan-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold text-sm md:text-base w-full sm:w-auto flex items-center justify-center gap-2"
              style={{ fontFamily: "Bambino" }}
            >
              <Download className="w-4 h-4" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-purple-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-t-purple-500 border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
            <span className="text-lg font-semibold text-gray-700" style={{ fontFamily: "Bambino" }}>
              Loading your report...
            </span>
          </div>
        )}

        {/* Report Data Display */}
        {reportData && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Summary Card */}
              <div className="lg:col-span-1">
                <div className="bg-gradient-to-br from-orange-100 via-red-100 to-pink-100 rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-orange-200 lg:sticky lg:top-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                    <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-orange-600" />
                    Summary
                  </h3>

                  <div className="space-y-3 md:space-y-4">
                    {/* Period */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-orange-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                        Period
                      </div>
                      <div className="text-sm md:text-base font-bold text-gray-800 break-words" style={{ fontFamily: "Bambino" }}>
                        {currentReportType === "daily" && selectedDate}
                        {currentReportType === "weekly" && `${weekStartDate} → ${calculateSundayFromMonday(weekStartDate)}`}
                        {currentReportType === "range" && `${rangeStart} → ${rangeEnd}`}
                        {currentReportType === "monthly" && `${getMonthName(selectedMonth - 1)} ${selectedYear}`}
                      </div>
                    </div>

                    {/* Total Sales */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-blue-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <BarChart3 className="w-3 h-3 md:w-4 md:h-4" />
                        Total Sales
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-blue-600" style={{ fontFamily: "Bambino" }}>
                        £{parseFloat(reportData.total_sales).toFixed(2)}
                      </div>
                    </div>

                    {/* Total Paidout */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-orange-300">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <span>£</span>
                        Total Paidout
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-orange-600" style={{ fontFamily: "Bambino" }}>
                        £{parseFloat(reportData.total_paidout).toFixed(2)}
                      </div>
                    </div>

                    {/* Net Sales */}
                    <div className="bg-gradient-to-br from-green-200 to-emerald-200 rounded-xl md:rounded-2xl p-3 md:p-4 border-2 border-green-300">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <span>£</span>
                        Net Sales
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-green-700" style={{ fontFamily: "Bambino" }}>
                        £{parseFloat(reportData.net_sales).toFixed(2)}
                      </div>
                    </div>

                    {/* Paidout Count */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl md:rounded-2xl p-3 md:p-4 border border-purple-200">
                      <div className="text-xs md:text-sm text-gray-600 mb-1 font-medium flex items-center gap-1" style={{ fontFamily: "Bambino" }}>
                        <Package className="w-3 h-3 md:w-4 md:h-4" />
                        Paidout Count
                      </div>
                      <div className="text-xl md:text-2xl font-bold text-purple-600" style={{ fontFamily: "Bambino" }}>
                        {reportData.paidout_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Card */}
              <div className="lg:col-span-2">
                <div className="bg-white/70 backdrop-blur-md rounded-3xl p-4 md:p-6 shadow-xl border-2 border-orange-100">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                      <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                      Paidout Details
                    </h3>
                    <button
                      onClick={() => setShowTable(!showTable)}
                      className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-orange-400 to-red-400 text-white rounded-lg md:rounded-xl hover:from-orange-500 hover:to-red-500 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold flex items-center gap-2 text-sm md:text-base w-full sm:w-auto justify-center"
                      style={{ fontFamily: "Bambino" }}
                    >
                      {showTable ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Hide
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Show
                        </>
                      )}
                    </button>
                  </div>

                  {showTable && (
                    <div className="overflow-x-auto rounded-lg md:rounded-xl -mx-4 md:mx-0">
                      <div className="inline-block min-w-full align-middle px-4 md:px-0">
                        <table className="w-full border-collapse min-w-[500px]">
                          <thead>
                            <tr className="bg-gradient-to-r from-orange-100 to-red-100">
                              <th className="border-2 border-orange-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Date</th>
                              <th className="border-2 border-orange-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Label</th>
                              <th className="border-2 border-orange-200 p-3 md:p-4 text-right font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody style={{ fontFamily: "Bambino" }}>
                            {reportData.paidouts?.map((payout, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-orange-50/30'}>
                                <td className="border border-orange-100 p-3 md:p-4 text-xs md:text-base">
                                  {new Date(payout.payout_date).toLocaleDateString('en-GB')}
                                </td>
                                <td className="border border-orange-100 p-3 md:p-4 font-semibold text-xs md:text-base">{payout.label}</td>
                                <td className="border border-orange-100 p-3 md:p-4 text-right font-bold text-orange-600 text-xs md:text-base">
                                  £{parseFloat(payout.amount).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Paidout Summary by Label */}
            {reportData.paidout_summary && reportData.paidout_summary.length > 0 && (
              <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-red-100">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                  <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-red-500" />
                  Summary by Label
                </h3>
                <div className="overflow-x-auto rounded-lg md:rounded-xl -mx-4 md:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 md:px-0">
                    <table className="w-full border-collapse min-w-[500px]">
                      <thead>
                        <tr className="bg-gradient-to-r from-red-100 to-orange-100">
                          <th className="border-2 border-red-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Label</th>
                          <th className="border-2 border-red-200 p-3 md:p-4 text-center font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Count</th>
                          <th className="border-2 border-red-200 p-3 md:p-4 text-right font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody style={{ fontFamily: "Bambino" }}>
                        {reportData.paidout_summary.map((summary, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-red-50/30'}>
                            <td className="border border-red-100 p-3 md:p-4 font-semibold text-xs md:text-base">{summary.label}</td>
                            <td className="border border-red-100 p-3 md:p-4 text-center text-xs md:text-base">{summary.count}</td>
                            <td className="border border-red-100 p-3 md:p-4 text-right font-bold text-red-600 text-xs md:text-base">
                              £{parseFloat(summary.total_amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Daily Totals */}
            {reportData.daily_totals && reportData.daily_totals.length > 0 && (
              <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-xl border-2 border-pink-100">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                  <Calendar className="w-6 h-6 md:w-8 md:h-8 text-pink-500" />
                  Daily Totals
                </h3>
                <div className="overflow-x-auto rounded-lg md:rounded-xl -mx-4 md:mx-0">
                  <div className="inline-block min-w-full align-middle px-4 md:px-0">
                    <table className="w-full border-collapse min-w-[500px]">
                      <thead>
                        <tr className="bg-gradient-to-r from-pink-100 to-red-100">
                          <th className="border-2 border-pink-200 p-3 md:p-4 text-left font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Date</th>
                          <th className="border-2 border-pink-200 p-3 md:p-4 text-center font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Count</th>
                          <th className="border-2 border-pink-200 p-3 md:p-4 text-right font-bold text-gray-700 text-xs md:text-base" style={{ fontFamily: "Bambino" }}>Daily Total</th>
                        </tr>
                      </thead>
                      <tbody style={{ fontFamily: "Bambino" }}>
                        {reportData.daily_totals.map((daily, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white/50' : 'bg-pink-50/30'}>
                            <td className="border border-pink-100 p-3 md:p-4 text-xs md:text-base">
                              {new Date(daily.date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="border border-pink-100 p-3 md:p-4 text-center text-xs md:text-base">{daily.count}</td>
                            <td className="border border-pink-100 p-3 md:p-4 text-right font-bold text-pink-600 text-xs md:text-base">
                              £{parseFloat(daily.daily_total).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        { !reportData && !isLoading && (
          <div className="bg-white/70 backdrop-blur-md rounded-3xl p-12 shadow-xl text-center border-2 border-orange-100">
            <span>£</span>
            <p className="text-gray-500 text-lg" style={{ fontFamily: "Bambino" }}>
              Select dates to view paidout details
            </p>
          </div>
        )}

        {/* PDF Export Hidden Section */}
        <div
          ref={pdfRef}
          id="pdf-content"
          style={{
            width: "210mm",
            maxWidth: "210mm",
            minHeight: "auto",
            padding: "15mm",
            backgroundColor: "#fff",
            color: "#000",
            fontFamily: "Arial, sans-serif",
            display: "none",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "bold" }}>
              Paidout Report
            </h1>
            <img style={{ height: "64px", width: "auto" }} src={logo} alt="Logo" />
          </div>

          {reportData && (
            <div style={{ marginBottom: "30px" }}>
              <p style={{ margin: "5px 0" }}>
                <strong>Period:</strong> {currentReportType === "daily" && selectedDate}
                {currentReportType === "weekly" && `${weekStartDate} to ${calculateSundayFromMonday(weekStartDate)}`}
                {currentReportType === "range" && `${rangeStart} to ${rangeEnd}`}
                {currentReportType === "monthly" && `${getMonthName(selectedMonth - 1)} ${selectedYear}`}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Total Sales:</strong> £{parseFloat(reportData.total_sales).toFixed(2)}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Total Paidout:</strong> £{parseFloat(reportData.total_paidout).toFixed(2)}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Net Sales:</strong> £{parseFloat(reportData.net_sales).toFixed(2)}
              </p>
            </div>
          )}

          <div id="dashboard-section" style={{ marginBottom: "20px" }}>
            {reportData?.paidout_summary && (
              <div style={{ marginBottom: "20px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "10px" }}>Paidout Summary</h2>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f0f0f0" }}>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "left" }}>Label</th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>Count</th>
                      <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.paidout_summary.map((summary, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                        <td style={{ border: "1px solid #ddd", padding: "8px" }}>{summary.label}</td>
                        <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center" }}>{summary.count}</td>
                        <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "right" }}>£{parseFloat(summary.total_amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaidoutReport;